// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../supabaseClient';
import type { Session } from '@supabase/supabase-js';
import type { Profile } from '../../types';
import { format } from 'date-fns';
import { AdminConfirmDialog } from './AdminConfirmDialog';
import { useAdminToast } from './useAdminToast';

interface AdminMediaLibraryProps {
  session: Session;
  profile: Profile | null;
  onSelect?: (url: string) => void;
  selectMode?: boolean;
}

interface MediaFile {
  name: string;
  id: string;
  created_at: string;
  updated_at: string;
  metadata: {
    size: number;
    mimetype: string;
  };
  url?: string;
}

export const AdminMediaLibrary: React.FC<AdminMediaLibraryProps> = ({
  session,
  profile,
  onSelect,
  selectMode = false,
}) => {
  const [files, setFiles] = useState<MediaFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | 'bulk' | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showSuccess, showError } = useAdminToast();

  useEffect(() => {
    loadFiles();
  }, []);

  const loadFiles = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.storage
        .from('documents')
        .list('', {
          limit: 100,
          offset: 0,
          sortBy: { column: 'created_at', order: 'desc' },
        });

      if (error) throw error;

      const filesWithUrls = await Promise.all(
        (data || []).map(async (file) => {
          const { data: urlData } = supabase.storage
            .from('documents')
            .getPublicUrl(file.name);
          return {
            ...file,
            url: urlData.publicUrl,
          };
        })
      );

      setFiles(filesWithUrls as MediaFile[]);
    } catch (err) {
      console.error('Error loading files:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !session?.user) return;

    setUploading(true);
    try {
      const filePath = `${session.user.id}/${Date.now()}-${file.name}`;
      const { error } = await supabase.storage
        .from('documents')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (error) throw error;
      await loadFiles();
    } catch (err) {
      console.error('Error uploading file:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (fileName: string) => {
    setDeleteTarget(fileName);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget || deleteTarget === 'bulk') return;

    try {
      const { error } = await supabase.storage.from('documents').remove([deleteTarget]);
      if (error) throw error;
      await loadFiles();
      showSuccess('File deleted successfully');
    } catch (err: any) {
      console.error('Error deleting file:', err);
      showError(err.message || 'Failed to delete file');
    } finally {
      setShowDeleteDialog(false);
      setDeleteTarget(null);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedFiles.length === 0) return;
    setDeleteTarget('bulk');
    setShowDeleteDialog(true);
  };

  const confirmBulkDelete = async () => {
    try {
      const { error } = await supabase.storage.from('documents').remove(selectedFiles);
      if (error) throw error;
      setSelectedFiles([]);
      await loadFiles();
      showSuccess(`${selectedFiles.length} file(s) deleted successfully`);
    } catch (err: any) {
      console.error('Error deleting files:', err);
      showError(err.message || 'Failed to delete files');
    } finally {
      setShowDeleteDialog(false);
      setDeleteTarget(null);
    }
  };

  const isImage = (mimetype: string) => {
    return mimetype?.startsWith('image/');
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-luna-secondary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4 sm:mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-luna-text-light dark:text-luna-text-dark mb-2">
            Media Library
          </h1>
          <p className="text-sm sm:text-base text-luna-text-light/70 dark:text-luna-text-dark/70">
            Manage uploaded files and media
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <label className="w-full sm:w-auto px-4 py-2.5 sm:py-2 bg-luna-secondary text-white rounded-lg hover:bg-luna-secondary/90 active:bg-luna-secondary/80 cursor-pointer transition-colors min-h-[44px] sm:min-h-[40px] flex items-center justify-center touch-manipulation">
            <input
              type="file"
              onChange={handleUpload}
              className="hidden"
              disabled={uploading}
            />
            {uploading ? 'Uploading...' : 'Upload File'}
          </label>
          {selectedFiles.length > 0 && (
            <button
              onClick={handleBulkDelete}
              className="w-full sm:w-auto px-4 py-2.5 sm:py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 active:bg-red-700 transition-colors min-h-[44px] sm:min-h-[40px] touch-manipulation"
            >
              Delete ({selectedFiles.length})
            </button>
          )}
          <button
            onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
            className="w-full sm:w-auto px-4 py-2.5 sm:py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 active:bg-gray-700 transition-colors min-h-[44px] sm:min-h-[40px] touch-manipulation flex items-center justify-center"
          >
            <span className="material-symbols-outlined">
              {viewMode === 'grid' ? 'view_list' : 'grid_view'}
            </span>
          </button>
        </div>
      </div>

      {/* Files Grid/List */}
      {files.length === 0 ? (
        <div className="bg-white dark:bg-luna-primary rounded-xl border-2 border-luna-secondary/30 p-12 text-center">
          <span className="material-symbols-outlined text-6xl text-luna-text-light/30 dark:text-luna-text-dark/30 mb-4">
            folder_open
          </span>
          <p className="text-luna-text-light/70 dark:text-luna-text-dark/70">
            No files uploaded yet
          </p>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4">
          {files.map((file) => (
            <div
              key={file.id}
              className={`bg-white dark:bg-luna-primary rounded-xl border-2 border-luna-secondary/30 overflow-hidden cursor-pointer hover:border-luna-secondary active:border-luna-secondary transition-colors touch-manipulation ${
                selectedFiles.includes(file.name) ? 'border-luna-secondary' : ''
              }`}
              onClick={() => {
                if (selectMode && onSelect && file.url) {
                  onSelect(file.url);
                } else {
                  setSelectedFiles((prev) =>
                    prev.includes(file.name)
                      ? prev.filter((f) => f !== file.name)
                      : [...prev, file.name]
                  );
                }
              }}
            >
              {isImage(file.metadata?.mimetype) && file.url ? (
                <img
                  src={file.url}
                  alt={file.name}
                  className="w-full h-24 sm:h-32 object-cover"
                />
              ) : (
                <div className="w-full h-24 sm:h-32 bg-luna-secondary/10 flex items-center justify-center">
                  <span className="material-symbols-outlined text-3xl sm:text-4xl text-luna-secondary">
                    description
                  </span>
                </div>
              )}
              <div className="p-2">
                <p className="text-xs font-medium text-luna-text-light dark:text-luna-text-dark truncate">
                  {file.name}
                </p>
                <p className="text-xs text-luna-text-light/50 dark:text-luna-text-dark/50">
                  {formatFileSize(file.metadata?.size || 0)}
                </p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white dark:bg-luna-primary rounded-xl border-2 border-luna-secondary/30 overflow-hidden">
          <div className="overflow-x-auto -mx-4 sm:mx-0 overscroll-x-contain touch-pan-x">
            <div className="inline-block min-w-full px-4 sm:px-0">
              <table className="w-full min-w-[600px] sm:min-w-0">
                <thead className="bg-luna-secondary/10">
                  <tr>
                    <th className="px-3 sm:px-4 py-3 text-left text-xs sm:text-sm font-semibold text-luna-text-light dark:text-luna-text-dark">
                      <input
                        type="checkbox"
                        checked={selectedFiles.length === files.length && files.length > 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedFiles(files.map((f) => f.name));
                          } else {
                            setSelectedFiles([]);
                          }
                        }}
                        className="w-5 h-5 sm:w-4 sm:h-4 rounded border-luna-secondary/30 touch-manipulation"
                      />
                    </th>
                    <th className="px-3 sm:px-4 py-3 text-left text-xs sm:text-sm font-semibold text-luna-text-light dark:text-luna-text-dark">
                      Name
                    </th>
                    <th className="px-3 sm:px-4 py-3 text-left text-xs sm:text-sm font-semibold text-luna-text-light dark:text-luna-text-dark">
                      Size
                    </th>
                    <th className="px-3 sm:px-4 py-3 text-left text-xs sm:text-sm font-semibold text-luna-text-light dark:text-luna-text-dark">
                      Type
                    </th>
                    <th className="px-3 sm:px-4 py-3 text-left text-xs sm:text-sm font-semibold text-luna-text-light dark:text-luna-text-dark">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {files.map((file) => (
                    <tr
                      key={file.id}
                      className="border-t border-luna-secondary/10 hover:bg-luna-secondary/5 active:bg-luna-secondary/10"
                    >
                      <td className="px-3 sm:px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedFiles.includes(file.name)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedFiles([...selectedFiles, file.name]);
                            } else {
                              setSelectedFiles(selectedFiles.filter((f) => f !== file.name));
                            }
                          }}
                          className="w-5 h-5 sm:w-4 sm:h-4 rounded border-luna-secondary/30 touch-manipulation"
                        />
                      </td>
                      <td className="px-3 sm:px-4 py-3 text-xs sm:text-sm text-luna-text-light dark:text-luna-text-dark">
                        {file.name}
                      </td>
                      <td className="px-3 sm:px-4 py-3 text-xs sm:text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
                        {formatFileSize(file.metadata?.size || 0)}
                      </td>
                      <td className="px-3 sm:px-4 py-3 text-xs sm:text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
                        {file.metadata?.mimetype || 'Unknown'}
                      </td>
                      <td className="px-3 sm:px-4 py-3 text-xs sm:text-sm">
                        <div className="flex gap-2">
                          {file.url && (
                            <a
                              href={file.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-2 py-1.5 sm:py-1 bg-blue-500 text-white rounded hover:bg-blue-600 active:bg-blue-700 text-xs min-h-[44px] sm:min-h-[32px] flex items-center touch-manipulation"
                            >
                              View
                            </a>
                          )}
                          <button
                            onClick={() => handleDelete(file.name)}
                            className="px-2 py-1.5 sm:py-1 bg-red-500 text-white rounded hover:bg-red-600 active:bg-red-700 text-xs min-h-[44px] sm:min-h-[32px] touch-manipulation"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AdminConfirmDialog
        isOpen={showDeleteDialog}
        title={deleteTarget === 'bulk' ? 'Delete Multiple Files' : 'Delete File'}
        message={
          deleteTarget === 'bulk'
            ? `Are you sure you want to delete ${selectedFiles.length} file(s)? This action cannot be undone.`
            : 'Are you sure you want to delete this file? This action cannot be undone.'
        }
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={deleteTarget === 'bulk' ? confirmBulkDelete : confirmDelete}
        onCancel={() => {
          setShowDeleteDialog(false);
          setDeleteTarget(null);
        }}
        variant="danger"
      />
    </div>
  );
};

